import { Timestamp } from 'firebase/firestore';
import {
  AlertCircle,
  BedDouble,
  BellRing,
  BrushCleaning,
  Calendar,
  Car,
  ClipboardList,
  Clock,
  ConciergeBell,
  LifeBuoy,
  Package,
  PackagePlus,
  Plane,
  Settings,
  ShoppingBag,
  Utensils,
} from 'lucide-react';

import { InAppNotification, InAppNotificationUI, TranslationType } from '../types';

export const getTranslatedContent = (
  content: TranslationType,
  preferredLanguage = 'pl',
): string => content[preferredLanguage as keyof TranslationType] || content.en || content.pl || '';

export const formatNotificationTime = (timestamp: Timestamp, language = 'pl'): string => {
  const now = new Date();
  const notificationDate = timestamp.toDate();
  const diffInMs = now.getTime() - notificationDate.getTime();
  const diffInMinutes = Math.floor(diffInMs / 60000);
  const diffInHours = Math.floor(diffInMs / 3600000);
  const diffInDays = Math.floor(diffInMs / 86400000);

  // Translations for different languages
  const translations: Record<string, any> = {
    pl: {
      now: 'Teraz',
      minAgo: (min: number) => `${min} min temu`,
      hourAgo: (hours: number) => `${hours}h temu`,
      dayAgo: '1 dzień temu',
      daysAgo: (days: number) => `${days} dni temu`,
      locale: 'pl-PL',
    },
    en: {
      now: 'Now',
      minAgo: (min: number) => `${min} min ago`,
      hourAgo: (hours: number) => `${hours}h ago`,
      dayAgo: '1 day ago',
      daysAgo: (days: number) => `${days} days ago`,
      locale: 'en-US',
    },
    de: {
      now: 'Jetzt',
      minAgo: (min: number) => `vor ${min} Min.`,
      hourAgo: (hours: number) => `vor ${hours} Std.`,
      dayAgo: 'vor 1 Tag',
      daysAgo: (days: number) => `vor ${days} Tagen`,
      locale: 'de-DE',
    },
    cs: {
      now: 'Nyní',
      minAgo: (min: number) => `před ${min} min`,
      hourAgo: (hours: number) => `před ${hours}h`,
      dayAgo: 'před 1 dnem',
      daysAgo: (days: number) => `před ${days} dny`,
      locale: 'cs-CZ',
    },
    es: {
      now: 'Ahora',
      minAgo: (min: number) => `hace ${min} min`,
      hourAgo: (hours: number) => `hace ${hours}h`,
      dayAgo: 'hace 1 día',
      daysAgo: (days: number) => `hace ${days} días`,
      locale: 'es-ES',
    },
    fr: {
      now: 'Maintenant',
      minAgo: (min: number) => `il y a ${min} min`,
      hourAgo: (hours: number) => `il y a ${hours}h`,
      dayAgo: 'il y a 1 jour',
      daysAgo: (days: number) => `il y a ${days} jours`,
      locale: 'fr-FR',
    },
    it: {
      now: 'Ora',
      minAgo: (min: number) => `${min} min fa`,
      hourAgo: (hours: number) => `${hours}h fa`,
      dayAgo: '1 giorno fa',
      daysAgo: (days: number) => `${days} giorni fa`,
      locale: 'it-IT',
    },
    sk: {
      now: 'Teraz',
      minAgo: (min: number) => `pred ${min} min`,
      hourAgo: (hours: number) => `pred ${hours}h`,
      dayAgo: 'pred 1 dňom',
      daysAgo: (days: number) => `pred ${days} dňami`,
      locale: 'sk-SK',
    },
  };

  const t = translations[language] || translations.pl;

  if (diffInMinutes < 1) {
    return t.now;
  }
  if (diffInMinutes < 60) {
    return t.minAgo(diffInMinutes);
  }
  if (diffInHours < 24) {
    return t.hourAgo(diffInHours);
  }
  if (diffInDays === 1) {
    return t.dayAgo;
  }
  if (diffInDays < 7) {
    return t.daysAgo(diffInDays);
  }

  return notificationDate.toLocaleDateString(t.locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Map notification type to icon and colors
const getNotificationStyle = (orderType?: string, taskCategory?: string) => {
  // For task/report notifications, check taskCategory
  if (orderType === 'task' || orderType === 'report' || taskCategory) {
    switch (taskCategory) {
      case 'housekeeping':
        return {
          icon: BrushCleaning,
          iconColor: '#7c3aed',
          bgColor: '#f3eeff',
        };
      case 'technical':
        return {
          icon: Settings,
          iconColor: '#dc2626',
          bgColor: '#feedec',
        };
      case 'wakeup':
        return {
          icon: BellRing,
          iconColor: '#0284c7',
          bgColor: '#e0f2fe',
        };
      case 'guestAsk':
        return {
          icon: ConciergeBell,
          iconColor: '#ea580c',
          bgColor: '#fed7aa',
        };
      case 'airportTransfer':
        return {
          icon: Plane,
          iconColor: '#0b91b2',
          bgColor: '#cffafe',
        };
      case 'taxi':
        return {
          icon: Car,
          iconColor: '#f59e0b',
          bgColor: '#fef3c7',
        };
      case 'lateCheckout':
        return {
          icon: Clock,
          iconColor: '#4f46e6',
          bgColor: '#e0e7ff',
        };
      case 'minibar':
        return {
          icon: PackagePlus,
          iconColor: '#db2878',
          bgColor: '#fce7f3',
        };
      case 'pillowMenu':
        return {
          icon: BedDouble,
          iconColor: '#0d9488',
          bgColor: '#ccfbf1',
        };
      default:
        // 'others' or undefined
        return {
          icon: AlertCircle,
          iconColor: '#64748b',
          bgColor: '#f1f5f9',
        };
    }
  }

  // For order notifications and other types
  switch (orderType) {
    case 'room-service':
      return {
        icon: Utensils,
        iconColor: '#16a34a',
        bgColor: '#ecfaee',
      };
    case 'products-services':
      return {
        icon: Package,
        iconColor: '#ea580c',
        bgColor: '#fed7aa',
      };
    case 'event':
      return {
        icon: Calendar,
        iconColor: '#0284c7',
        bgColor: '#e0f2fe',
      };
    case 'rental':
      return {
        icon: ShoppingBag,
        iconColor: '#ca8a04',
        bgColor: '#fef3c7',
      };
    case 'survey':
      return {
        icon: ClipboardList,
        iconColor: '#16a34a',
        bgColor: '#dcfce7',
      };
    case 'support':
      return {
        icon: LifeBuoy,
        iconColor: '#1a4d4d',
        bgColor: '#e6f0f0',
      };
    default:
      return {
        icon: AlertCircle,
        iconColor: '#64748b',
        bgColor: '#f1f5f9',
      };
  }
};

export const mapInAppNotificationToUI = (
  notification: InAppNotification,
  language = 'pl',
): InAppNotificationUI => {
  const title = getTranslatedContent(notification.title, language);
  const description = getTranslatedContent(notification.description, language);
  const time = formatNotificationTime(notification.createdAt, language);

  // Determine notification type - check for taskId to identify task notifications
  const notificationType = notification.metadata?.taskId ? 'task' : notification.metadata?.orderType;
  const style = getNotificationStyle(notificationType, notification.metadata?.taskCategory);

  return {
    id: notification.id,
    title,
    description,
    icon: style.icon,
    iconColor: style.iconColor,
    bgColor: style.bgColor,
    time,
    isRead: notification.isRead,
    roomNumber: notification.metadata?.roomNumber?.toString(),
    metadata: notification.metadata,
  };
};
